import "./studentDashboard.css";
import { createPortal } from "react-dom";
import {
  useState,
  useEffect,
  useRef,
} from "react";
import { useNavigate,
  useSearchParams,
} from "react-router-dom";
import api from "./api/axios";

import {
  FiImage,
  FiHeart,
  FiSend,
  FiMessageCircle,
  FiX,
  FiUsers,
  FiCamera,
  FiBookOpen,
  FiMoreHorizontal,
  FiSmile,
  FiBookmark,
  FiArrowLeft,
  FiTrash2,
} from "react-icons/fi";

import useAuth from "./hooks/useAuth";

function StudentDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
const isFaculty = user?.role === "faculty";
  const [content, setContent] = useState("");
  const [image, setImage] = useState(null);

  const fileInputRef = useRef(null);
const [announcements, setAnnouncements] = useState([]);
const [isAnnouncementCompact, setIsAnnouncementCompact] = useState(false);
const announcementSectionRef = useRef(null);  
const [selectedPost, setSelectedPost] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
const [openPostMenu, setOpenPostMenu] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentsMap, setCommentsMap] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
const [replyText, setReplyText] = useState("");
  const [posts, setPosts] = useState([]);
const [showLikesModal, setShowLikesModal] = useState(false);
const [likesModalPost, setLikesModalPost] = useState(null);
  const [isPosting, setIsPosting] = useState(false);
const [showDeleteModal, setShowDeleteModal] = useState(false);
const [postToDelete, setPostToDelete] = useState(null);


  const fetchAnnouncements = async () => {
  if (!user?.id) return;

  try {
    const res = await api.get(
  `/api/announcements/student/${user.id}`
);

    setAnnouncements(
      Array.isArray(res.data) ? res.data : []
    );
  } catch (err) {
    console.error("Failed to load announcements:", err);
  }
};
  /* =========================================================
     TIME FORMAT
     ========================================================= */

  const formatTime = (time) => {
    if (!time) return "";

    const date = new Date(time);
    const now = new Date();

    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const navigate = useNavigate();

const openStudentProfile = (student) => {
  const studentId = student?.user_id || student?.id;

  if (!studentId) return;

  navigate(`/student/${studentId}`);
};

const openLikesModal = (post) => {
  if (!post?.reactors?.length) return;

  setLikesModalPost(post);
  setShowLikesModal(true);
};

const closeLikesModal = () => {
  setShowLikesModal(false);
  setLikesModalPost(null);
};

const goToAnnouncements = () => {
  const announcement = announcementSectionRef.current;

  if (!announcement) {
    console.error("Announcement section was not found.");
    return;
  }

  const navbar = document.querySelector(".student-navbar");
  const navbarHeight =
    navbar?.getBoundingClientRect().height ??
    76;

  const targetTop =
    window.scrollY +
    announcement.getBoundingClientRect().top -
    navbarHeight -
    12;

  window.scrollTo({
    top: Math.max(0, targetTop),
    behavior: "smooth",
  });
};


  // ============================================================
// BUILD NESTED COMMENT TREE
// ============================================================
const buildCommentTree = (comments) => {
  const commentMap = {};
  const roots = [];

  // ------------------------------------------------------------
  // CREATE EVERY COMMENT IN MAP
  // ------------------------------------------------------------
  comments.forEach((comment) => {
    commentMap[comment.id] = {
      ...comment,
      replies: [],
    };
  });

  // ------------------------------------------------------------
  // CONNECT REPLIES TO THEIR PARENTS
  // ------------------------------------------------------------
  comments.forEach((comment) => {
    if (
      comment.parent_comment_id
    ) {
      const parent =
        commentMap[
          comment.parent_comment_id
        ];

      if (parent) {
        parent.replies.push(
          commentMap[comment.id]
        );
      }
    } else {
      roots.push(
        commentMap[comment.id]
      );
    }
  });

  return roots;
};

// ============================================================
// COUNT ALL COMMENTS + NESTED REPLIES
// ============================================================
const countComments = (comments) => {
  if (!Array.isArray(comments)) return 0;

  return comments.reduce((total, comment) => {
    return (
      total +
      1 +
      countComments(comment.replies)
    );
  }, 0);
};

const loadComments = async (postId) => {
  try {
    const res = await api.get(
  `/api/posts/${postId}/comments`
);

    const rows =
      Array.isArray(res.data)
        ? res.data
        : Array.isArray(
            res.data?.comments
          )
        ? res.data.comments
        : [];

    const commentTree =
      buildCommentTree(rows);

    setCommentsMap((prev) => ({
      ...prev,
      [postId]: commentTree,
    }));
  } catch (err) {
    console.error(
      "? Failed to load comments:",
      err
    );
  }
};

  /* =========================================================
     GET INITIAL
     ========================================================= */

  const getInitial = (name) => {
    return name?.charAt(0)?.toUpperCase() || "S";
  };

  /* =========================================================
     FETCH POSTS
     ========================================================= */

const fetchPosts = async () => {
  try {
const res = await api.get(
  "/api/posts",
  {
    params: {
      user_id: user.id,
    },
  }
);

    const fetchedPosts = Array.isArray(res.data)
  ? res.data.map((post) => ({
      ...post,
      liked: Boolean(post.liked),
      likes: Number(post.likes || 0),
      reactors: Array.isArray(post.reactors)
        ? post.reactors
        : [],
    }))
  : [];

    setPosts(fetchedPosts);

    const commentsData = {};

await Promise.all(
  fetchedPosts.map(async (post) => {
    try {
const comments = await api.get(
  `/api/posts/${post.id}/comments`
);

      const rows = Array.isArray(comments.data)
        ? comments.data
        : Array.isArray(comments.data?.comments)
        ? comments.data.comments
        : [];

      commentsData[post.id] =
        buildCommentTree(rows);
    } catch (err) {
      console.error(
        `Failed to fetch comments for post ${post.id}`,
        err
      );

      commentsData[post.id] = [];
    }
  })
);

    setCommentsMap(commentsData);
  } catch (err) {
    console.error("Failed to load posts:", err);
  }
};

useEffect(() => {
  if (user?.id) {
    fetchPosts();
    fetchAnnouncements();
  }
}, [user?.id]);


useEffect(() => {
  const handleScroll = () => {
    const announcement = document.querySelector(
      ".announcement-section"
    );

    if (!announcement) {
      setIsAnnouncementCompact(false);
      return;
    }

    // Show the floating bar once the normal announcement
    // section has scrolled completely out of view.
    const rect = announcement.getBoundingClientRect();

    setIsAnnouncementCompact(rect.bottom <= 0);
  };

  window.addEventListener("scroll", handleScroll, {
    passive: true,
  });

  // Also detect scrolling inside nested containers.
  document.addEventListener("scroll", handleScroll, true);
  window.addEventListener("resize", handleScroll);

  handleScroll();

  return () => {
    window.removeEventListener("scroll", handleScroll);
    document.removeEventListener("scroll", handleScroll, true);
    window.removeEventListener("resize", handleScroll);
  };
}, []);


useEffect(() => {
  const updateNavbarHeight = () => {
    const navbar = document.querySelector(".student-navbar");

    if (navbar) {
      const height = navbar.getBoundingClientRect().height;

      document.documentElement.style.setProperty(
        "--student-navbar-height",
        `${height}px`
      );
    }
  };

  updateNavbarHeight();

  window.addEventListener("resize", updateNavbarHeight);

  return () => {
    window.removeEventListener("resize", updateNavbarHeight);
  };
}, []);



  /* =========================================================
     OPEN POST / COMMENTS
     ========================================================= */

const openPost = async (post) => {
  setSelectedPost(post);
  setReplyingTo(null);
  setReplyText("");

  try {
    const res = await api.get(
  `/api/posts/${post.id}/comments`
);

    const rows = Array.isArray(res.data)
      ? res.data
      : Array.isArray(res.data?.comments)
      ? res.data.comments
      : [];

    const commentTree =
      buildCommentTree(rows);

    setCommentsMap((prev) => ({
      ...prev,
      [post.id]: commentTree,
    }));
  } catch (err) {
    console.error(
      "Failed to load comments:",
      err
    );
  }
};

  /* =========================================================
     REACT TO POST
     ========================================================= */

const handleReact = async (postId) => {
  if (!user?.id) return;

  try {
    const res = await api.post(
      `/api/posts/${postId}/react`,
      {
        user_id: user.id,
      }
    );

    const liked = Boolean(res.data.liked);
    const likes = Number(res.data.likes || 0);

    const reactors = Array.isArray(res.data.reactors)
      ? res.data.reactors
      : [];

    // Update feed
    setPosts((prev) =>
      prev.map((post) =>
        post.id === postId
          ? {
              ...post,
              liked,
              likes,
              reactors,
            }
          : post
      )
    );

    // Update modal
    setSelectedPost((prev) => {
      if (!prev || prev.id !== postId) {
        return prev;
      }

      return {
        ...prev,
        liked,
        likes,
        reactors,
      };
    });
  } catch (err) {
    console.error(
      "Failed to react to post:",
      err
    );
  }
};

const handleDeleteClick = (post) => {
  setOpenPostMenu(null);
  setPostToDelete(post);
  setShowDeleteModal(true);
};

const handleConfirmDelete = async () => {
  if (!postToDelete?.id || !user?.id) return;

  try {
    await api.delete(`/api/posts/${postToDelete.id}`, {
      data: {
        user_id: user.id,
      },
    });

    setPosts((prev) =>
      prev.filter((post) => post.id !== postToDelete.id)
    );

    setCommentsMap((prev) => {
      const updated = { ...prev };
      delete updated[postToDelete.id];
      return updated;
    });

    if (selectedPost?.id === postToDelete.id) {
      setSelectedPost(null);
    }

    setShowDeleteModal(false);
    setPostToDelete(null);
    setOpenPostMenu(null);

  } catch (err) {
    console.error("Failed to delete post:", err);

    alert(
      err.response?.data?.message ||
        "Failed to delete post."
    );
  }
};

  /* =========================================================
     COMMENT
     ========================================================= */

const handleComment = async () => {
  if (
    !selectedPost ||
    !commentText.trim() ||
    !user?.id
  ) {
    return;
  }

  try {
    const postId = selectedPost.id;
    const newComment = commentText.trim();

    await api.post(
  `/api/posts/${postId}/comment`,
  {
    user_id: user.id,
    comment: newComment,
    parent_comment_id: null,
  }
);

    // Clear comment input
    setCommentText("");

    // Reload comments so the comment tree stays updated
    await loadComments(postId);
  } catch (err) {
    console.error(
      "? Failed to comment:",
      err
    );

    const message =
      err.response?.data?.message ||
      "Failed to post comment.";

    alert(message);
  }
};
const handleReply = async (commentId) => {
  if (
    !selectedPost ||
    !replyText.trim() ||
    !user?.id ||
    !commentId
  ) {
    return;
  }

  try {
    const postId =
      selectedPost.id;

    const newReplyText =
      replyText.trim();

    await api.post(
  `/api/posts/${postId}/comment`,
      {
        user_id: user.id,
        comment: newReplyText,
        parent_comment_id:
          commentId,
      }
    );

    console.log(
      "? Reply created successfully."
    );

    setReplyText("");
    setReplyingTo(null);

    // Reload comments.
    // This also correctly handles nested replies.
    await loadComments(postId);
  } catch (err) {
    console.error(
      "? Failed to reply:",
      err
    );

    const message =
      err.response?.data?.message ||
      "Failed to reply.";

    alert(message);
  }
};

const cancelReply = () => {
  setReplyingTo(null);
  setReplyText("");
};

// ============================================================
// POST AUTHOR META
// ============================================================
const getPostMeta = (post) => {
  if (post?.role === "faculty") {
    return post.department_program
      ? `${post.department_program} . Faculty`
      : "Faculty";
  }

  const batch = post?.batch;

  if (batch && batch !== "NULL") {
    return `Class of ${batch}`;
  }

  return "Student";
};

const renderComment = (comment, level = 0) => {
  if (!comment) return null;


  return (
    <div
      className={`comment-thread ${
        level > 0 ? "nested-comment-thread" : ""
      }`}
      key={comment.id}
    >
      {/* COMMENT / REPLY */}
      <div
        className={`comment-item ${
          level > 0 ? "reply-item" : ""
        }`}
      >
        <button
  type="button"
  className="avatar tiny profile-avatar-button"
  onClick={() => openStudentProfile(comment)}
  aria-label={`View ${comment.full_name}'s profile`}
>
  {comment.profile_pic ? (
    <img
      src={comment.profile_pic}
      alt={comment.full_name}
    />
  ) : (
    <span>
      {getInitial(comment.full_name)}
    </span>
  )}
</button>

        <div className="comment-content">
          <button
  type="button"
  className="comment-author-name"
  onClick={() => openStudentProfile(comment)}
>
  {comment.full_name}
</button>

          <p>
            {comment.comment}
          </p>

          <div className="comment-actions">
            <button
              type="button"
              onClick={() => {
                setReplyingTo(comment.id);
                setReplyText("");
              }}
            >
              Reply
            </button>
          </div>
        </div>
      </div>

      {/* REPLY INPUT */}
      {replyingTo === comment.id && (
        <div className="reply-input">
          <div className="avatar tiny">
            {user?.profile_pic ? (
              <img
                src={user.profile_pic}
                alt=""
              />
            ) : (
              <span>
                {getInitial(user?.full_name)}
              </span>
            )}
          </div>

          <div className="reply-input-box">
            <input
              type="text"
              value={replyText}
              onChange={(e) =>
                setReplyText(e.target.value)
              }
              placeholder={`Reply to ${comment.full_name}...`}
              autoFocus
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey
                ) {
                  e.preventDefault();
                  handleReply(comment.id);
                }

                if (e.key === "Escape") {
                  cancelReply();
                }
              }}
            />

            <button
              type="button"
              onClick={() =>
                handleReply(comment.id)
              }
              disabled={!replyText.trim()}
            >
              <FiSend />
            </button>

            <button
              type="button"
              className="cancel-reply"
              onClick={cancelReply}
            >
              <FiX />
            </button>
          </div>
        </div>
      )}

      {/* NESTED REPLIES */}
      {Array.isArray(comment.replies) &&
        comment.replies.length > 0 && (
          <div className="comment-replies">
            {comment.replies.map((reply) =>
              renderComment(
                reply,
                level + 1
              )
            )}
          </div>
        )}
    </div>
  );
};

  /* =========================================================
     SELECT IMAGE
     ========================================================= */

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setImage(file);
    setShowCreateModal(true);
  };

  /* =========================================================
     CREATE POST
     ========================================================= */

  const handlePost = async () => {
    if (!user?.id) return;

    if (!content.trim() && !image) return;

    try {
      setIsPosting(true);

      let imageUrl = "";

      /* Upload image */

      if (image) {
        const formData = new FormData();

        formData.append("image", image);

const uploadRes = await api.post(
  "/api/upload",
  formData
);

        imageUrl = uploadRes.data.imageUrl;
      }

      /* Save post */

const postRes = await api.post(
  "/api/posts",
        {
          user_id: user.id,
          content: content.trim(),
          image: imageUrl,
        }
      );

const newPost = {
  id: postRes.data?.id || Date.now(),
  user_id: user.id,
  full_name: user.full_name,
  profile_pic: user.profile_pic,
  batch: user.batch,
  role: user.role,
  department_program: user.department_program,
  content: content.trim(),
  image: imageUrl,
  created_at: new Date().toISOString(),
  likes: 0,
  liked: false,
};

      setPosts((prev) => [newPost, ...prev]);

      setCommentsMap((prev) => ({
        ...prev,
        [newPost.id]: [],
      }));

      /* Reset */

      setContent("");
      setImage(null);
      setShowCreateModal(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Failed to create post:", err);
    } finally {
      setIsPosting(false);
    }
  };

  /* =========================================================
     CANCEL CREATE
     ========================================================= */

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setImage(null);
    setContent("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* =========================================================
     CLOSE VIEW MODAL
     ========================================================= */

const closePostModal = () => {
  setSelectedPost(null);
  setCommentText("");
  setReplyText("");
  setReplyingTo(null);

  if (searchParams.get("post")) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("post");
      return next;
    });
  }
};

  useEffect(() => {
    const postId = Number(searchParams.get("post"));

    if (!postId || !posts.length) {
      return;
    }

    const postFromList = posts.find(
      (post) => Number(post.id) === postId
    );

    if (!postFromList) {
      return;
    }

    openPost(postFromList);
  }, [posts, searchParams]);

  /* =========================================================
     TOTAL COMMENTS
     ========================================================= */

  const totalComments = Object.values(commentsMap).reduce(
    (total, comments) =>
      total + (Array.isArray(comments) ? comments.length : 0),
    0
  );

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div className="yearbook-dashboard">

      {/* =====================================================
          HERO
          ===================================================== */}

      <section className="dashboard-hero">

        <div className="hero-content">

          <div className="hero-eyebrow">
            <FiBookOpen />
            <span>ADSSU DIGITAL YEARBOOK</span>
          </div>

          <h1>
  {isFaculty ? (
    <>
      Faculty <strong>Portal</strong>
    </>
  ) : (
    <>
      Class of{" "}
      <strong>{user?.batch || "----"}</strong>
    </>
  )}
</h1>

<p>
  Welcome back,{" "}
  <strong>
    {user?.full_name || (isFaculty ? "Faculty" : "Student")}
  </strong>
  .{" "}
  {isFaculty
    ? "Stay connected with the yearbook community."
    : "Share your moments and memories with your batch."}
</p>

          <div className="hero-meta">

<div>
  <FiUsers />
  <span>
    {isFaculty
      ? user?.department_program || "Faculty"
      : `Batch ${user?.batch || "----"}`}
  </span>
</div>

<div>
  <FiCamera />
  <span>
    {isFaculty
      ? "Faculty Community"
      : "Community Memories"}
  </span>
</div>

          </div>

        </div>

        <div className="hero-decoration">

          <div className="hero-card hero-card-back">
            <FiImage />
          </div>

          <div className="hero-card hero-card-middle">
            <FiHeart />
          </div>

          <div className="hero-card hero-card-front">

            <div className="mini-card-top">
              MEMORY
            </div>

            <div className="mini-card-title">
              Our Year
            </div>

            <div className="mini-card-line" />

<span>
  {isFaculty
    ? `${user?.department_program || "Faculty"} Faculty`
    : `Class of ${user?.batch || "----"}`}
</span>

          </div>

        </div>

      </section>

{announcements.length > 0 && (
<section
  ref={announcementSectionRef}
  className="announcement-section"
>

  <div className="announcement-compact-bar">
  <span className="compact-pin">📌</span>
  <span className="compact-megaphone">📣</span>

  <span className="compact-announcement-text">
    Announcements
  </span>

  <span className="compact-announcement-count">
    {announcements.length}
  </span>
</div>

    <div className="announcement-heading">
      <div>
        <span className="announcement-kicker">
          IMPORTANT UPDATES
        </span>

        <h2>Announcements</h2>

        <p>
          Stay updated with your yearbook participation.
        </p>
      </div>

      <span className="announcement-icon">
        📌
      </span>
    </div>

    <div className="announcement-list">
      {announcements.map((announcement) => (
        <article
          className={`announcement-card ${
            announcement.priority === "important"
              ? "announcement-important"
              : ""
          }`}
          key={announcement.id}
        >

          <div className="announcement-card-icon">
            📣
          </div>

          <div className="announcement-card-content">

            <div className="announcement-card-top">
              <span className="announcement-type">
                {announcement.type === "automatic"
                  ? "AUTOMATED REMINDER"
                  : "ANNOUNCEMENT"}
              </span>

              {announcement.priority === "important" && (
                <span className="announcement-priority">
                  Important
                </span>
              )}
            </div>

<h3>{announcement.title}</h3>

<p className="announcement-message">
  {announcement.message}
</p>

<div className="announcement-meta">
  <span className="announcement-date">
    {announcement.created_at
      ? new Date(
          announcement.created_at
        ).toLocaleDateString()
      : "Today"}
  </span>

  {announcement.deadline && (
    <span className="announcement-deadline">
      Deadline:{" "}
      {new Date(
        announcement.deadline
      ).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })}
    </span>
  )}
</div>

          </div>

        </article>
      ))}
    </div>

  </section>
)}



{isAnnouncementCompact &&
  announcements.length > 0 &&
  !selectedPost &&
  !showLikesModal &&
  !showCreateModal &&
  !showDeleteModal &&
  createPortal(
    <button
      type="button"
      className="announcement-floating-bar"
      title="Go to announcements"
      aria-label="Go to announcements"
      onClick={goToAnnouncements}
    >
      <span className="compact-pin">📌</span>
      <span className="compact-megaphone">📣</span>
      <span className="compact-announcement-text">
        Announcements
      </span>
      <span className="compact-announcement-count">
        {announcements.length}
      </span>
    </button>,
    document.body
  )}


      {/* =====================================================
          MAIN CONTENT
          ===================================================== */}

      <div className="dashboard-grid">

        <main className="community-column">

          {/* =================================================
              FEED HEADER
              ================================================= */}

          <div className="feed-heading">

            <div>
              <span className="feed-kicker">
                BATCH COMMUNITY
              </span>

              <h2>
                Memory Feed
              </h2>

              <p>
                See what your classmates are sharing.
              </p>
            </div>

            <div className="feed-count">
              {posts.length}
              <span>
                {posts.length === 1
                  ? " post"
                  : " posts"}
              </span>
            </div>

          </div>

          {/* =================================================
              CREATE POST CARD
              ================================================= */}

          <section className="facebook-composer">

            <div className="composer-top">

              <div className="avatar composer-avatar">

                {user?.profile_pic ? (
                  <img
                    src={user.profile_pic}
                    alt={user.full_name}
                  />
                ) : (
                  <span>
                    {getInitial(user?.full_name)}
                  </span>
                )}

              </div>

              <button
                type="button"
                className="composer-input"
                onClick={() =>
                  setShowCreateModal(true)
                }
              >
                <span>
                  What's on your mind?
                </span>
              </button>

            </div>

            <div className="composer-bottom">

              <button
                type="button"
                onClick={() =>
                  fileInputRef.current?.click()
                }
              >
                <FiImage />
                <span>Photo</span>
              </button>

              <button
                type="button"
                onClick={() =>
                  setShowCreateModal(true)
                }
              >
                <FiCamera />
                <span>Memory</span>
              </button>

              <input
                type="file"
                hidden
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageSelect}
              />

            </div>

          </section>

          {/* =================================================
              FEED
              ================================================= */}

          <div className="facebook-feed">

            {posts.length === 0 ? (

              <div className="empty-feed">

                <div className="empty-feed-icon">
                  <FiCamera />
                </div>

                <h3>
  {isFaculty
    ? "No posts yet"
    : "No memories yet"}
</h3>

<p>
  {isFaculty
    ? "Be the first to share something with the community."
    : "Be the first person in your batch to share a special moment."}
</p>

<button
  type="button"
  onClick={() => setShowCreateModal(true)}
>
  {isFaculty ? "Create a Post" : "Create a Memory"}
</button>

              </div>

            ) : (

              posts.map((post) => {

const comments = commentsMap[post.id] || [];
const commentCount = countComments(comments);

                const likeCount =
                  post.likes || 0;

                return (
                  <article
                    className="facebook-post"
                    key={post.id}
                  >

                    {/* =========================================
                        POST HEADER
                        ========================================= */}

                    <div className="facebook-post-header">

                      <div className="facebook-author">

<button
  type="button"
  className="avatar post-avatar profile-avatar-button"
  onClick={() => openStudentProfile(post)}
  aria-label={`View ${post.full_name}'s profile`}
>
  {post?.profile_pic ? (
    <img
      src={post.profile_pic}
      alt={post.full_name}
    />
  ) : (
    <span>
      {getInitial(post?.full_name)}
    </span>
  )}
</button>

                        <div className="facebook-author-info">

<button
  type="button"
  className="post-author-name"
  onClick={() => openStudentProfile(post)}
>
  {post.full_name}
</button>

                          <div className="post-meta">

<span>
  {getPostMeta(post)}
</span>

                            <span className="meta-dot">
                              .
                            </span>

                            <span>
                              {formatTime(
                                post.created_at
                              )}
                            </span>

                          </div>

                        </div>

                      </div>

                      {String(post.user_id) === String(user?.id) && (
  <div className="post-more-wrapper">
    <button
      type="button"
      className="post-more"
      aria-label="More options"
      onClick={(e) => {
        e.stopPropagation();

        setOpenPostMenu(
          openPostMenu === post.id ? null : post.id
        );
      }}
    >
      <FiMoreHorizontal />
    </button>

    {openPostMenu === post.id && (
      <div
        className="post-options-menu"
        onClick={(e) => e.stopPropagation()}
      >
<button
  type="button"
  className="post-option delete-option"
  onClick={() => handleDeleteClick(post)}
>
  <span className="post-option-icon">🗑️</span>
  <span>Delete Post</span>
</button>
      </div>
    )}
  </div>
)}

                    </div>

                    {/* =========================================
                        POST TEXT
                        ========================================= */}

                    {post.content && (
                      <div className="facebook-post-text">
                        {post.content}
                      </div>
                    )}

                    {/* =========================================
                        POST IMAGE
                        ========================================= */}

                    {post.image && (
                      <div
                        className="facebook-post-image"
                        onClick={() =>
                          openPost(post)
                        }
                      >
                        <img
                          src={post.image}
                          alt="Shared memory"
                        />
                      </div>
                    )}

      {/* =========================================
    REACTION SUMMARY
    ========================================= */}
{(likeCount > 0 || commentCount > 0) && (
  <div className="post-reaction-summary">

    <div className="reaction-left">

      {likeCount > 0 && (
        <button
          type="button"
          className="reaction-summary-button"
          onClick={() => openLikesModal(post)}
          aria-label={`View ${likeCount} people who liked this post`}
        >
          <span className="reaction-circle">
            <FiHeart />
          </span>

          <span>
            {likeCount}
          </span>
        </button>
      )}

    </div>

    <div className="reaction-right">
      {commentCount > 0 && (
        <button
          type="button"
          onClick={() => openPost(post)}
        >
          {commentCount}{" "}
          {commentCount === 1
            ? "comment"
            : "comments"}
        </button>
      )}
    </div>

  </div>
)}

                    {/* =========================================
                        POST ACTIONS
                        ========================================= */}

                    <div className="facebook-post-actions">

<button
  type="button"
  className={
    post.liked
      ? "post-action liked"
      : "post-action"
  }
  onClick={() => handleReact(post.id)}
>
  <FiHeart
    className="heart-icon"
    fill={post.liked ? "currentColor" : "none"}
  />
  <span>
    {post.liked ? "Liked" : "Like"}
  </span>
</button>

                      <button
                        type="button"
                        className="post-action"
                        onClick={() =>
                          openPost(post)
                        }
                      >
                        <FiMessageCircle />

                        <span>
                          Comment
                        </span>
                      </button>

                     
                     

                    </div>

{/* =========================================
    PEOPLE WHO LIKED
    ========================================= */}
{likeCount > 0 && post.reactors?.length > 0 && (
  <div className="people-liked">

    <div className="mini-like-icons">
      <span>
        <FiHeart />
      </span>

      {post.reactors.length > 1 && (
        <span>
          <FiHeart />
        </span>
      )}
    </div>

    <span className="people-liked-text">

      {post.reactors.slice(0, 2).map((reactor, index) => (
        <span key={reactor.user_id}>

          <button
            type="button"
            className="reactor-name"
            onClick={() => openStudentProfile(reactor)}
          >
            {reactor.full_name}
          </button>

          {index <
            Math.min(post.reactors.length, 2) - 1 &&
            ", "}

        </span>
      ))}

      {post.reactors.length > 2 && (
        <>
          {" and "}

          <button
            type="button"
            className="other-likes-button"
            onClick={() => openLikesModal(post)}
          >
            {post.reactors.length - 2}{" "}
            {post.reactors.length - 2 === 1
              ? "other"
              : "others"}
          </button>
        </>
      )}

    </span>
  </div>
)}
                    {/* =========================================
                        COMMENTS PREVIEW
                        ========================================= */}

                    {commentCount > 0 && (
                      <div className="comments-preview">

                        <button
                          type="button"
                          className="view-comments"
                          onClick={() =>
                            openPost(post)
                          }
                        >
                          View all{" "}
                          {commentCount}{" "}
                          {commentCount === 1
                            ? "comment"
                            : "comments"}
                        </button>

                        {comments
                          .slice(0, 2)
                          .map(
                            (
                              comment,
                              index
                            ) => (
                              <div
                                className="preview-comment"
                                key={index}
                              >

                                <div className="avatar comment-avatar">

                                  {comment.profile_pic ? (
                                    <img
                                      src={
                                        comment.profile_pic
                                      }
                                      alt=""
                                    />
                                  ) : (
                                    <span>
                                      {getInitial(
                                        comment.full_name
                                      )}
                                    </span>
                                  )}

                                </div>

                                <div className="preview-comment-body">

                                  <strong>
                                    {
                                      comment.full_name
                                    }
                                  </strong>

                                  <p>
                                    {
                                      comment.comment
                                    }
                                  </p>

                                </div>

                              </div>
                            )
                          )}

                      </div>
                    )}

                  </article>
                );
              })

            )}

          </div>

        </main>



      </div>

{/* =====================================================
    VIEW POST MODAL
    Different layout for text-only and photo posts
    ===================================================== */}
{selectedPost && (
  <div
    className={`memory-modal-overlay ${
      selectedPost.image
        ? "has-photo-post"
        : "text-only-post"
    }`}
    onClick={closePostModal}
  >
    <div
      className={`memory-modal ${
        selectedPost.image
          ? "photo-post-modal"
          : "text-post-modal"
      }`}
      onClick={(e) => e.stopPropagation()}
    >

      {/* =================================================
          MODAL TOP BAR
          ================================================= */}
      <div className="memory-modal-topbar">

        <button
          type="button"
          className="modal-back-button"
          onClick={closePostModal}
        >
          <FiArrowLeft />
        </button>

        <div className="modal-top-title">
          <span>
            {selectedPost.image
              ? "YEARBOOK MEMORY"
              : "BATCH POST"}
          </span>

          <strong>
{getPostMeta(selectedPost)}
          </strong>
        </div>

        <button
          type="button"
          className="memory-modal-close"
          onClick={closePostModal}
        >
          <FiX />
        </button>

      </div>

      {/* =================================================
          PHOTO POST MODAL
          ================================================= */}
      {selectedPost.image ? (
        <div className="memory-modal-content photo-modal-content">

          {/* PHOTO */}
          <div className="memory-modal-image photo-modal-image">
            <img
              src={selectedPost.image}
              alt="Shared memory"
            />
          </div>

          {/* DETAILS */}
          <div className="memory-modal-details">

            {/* AUTHOR */}
            <div className="modal-author">

              <div className="avatar modal-avatar">
                {selectedPost.profile_pic ? (
                  <img
                    src={selectedPost.profile_pic}
                    alt=""
                  />
                ) : (
                  <span>
                    {getInitial(
                      selectedPost.full_name
                    )}
                  </span>
                )}
              </div>

              <div className="modal-author-info">
                <h3>
                  {selectedPost.full_name}
                </h3>

                <span>
  {getPostMeta(selectedPost)}{" "}
  .{" "}
  {formatTime(selectedPost.created_at)}
</span>
              </div>

            </div>

            {/* CAPTION */}
            {selectedPost.content && (
              <div className="modal-story photo-caption">
                {selectedPost.content}
              </div>
            )}

            {/* REACTIONS */}
            <div className="modal-reactions">

<button
  type="button"
  className={
    selectedPost.liked
      ? "modal-react liked"
      : "modal-react"
  }
  onClick={() =>
    handleReact(selectedPost.id)
  }
>
  <FiHeart
    className="heart-icon"
    fill={
      selectedPost.liked
        ? "currentColor"
        : "none"
    }
  />

  <span>
    {selectedPost.likes || 0}
  </span>

  <small>
    {selectedPost.liked ? "Liked" : "Likes"}
  </small>
</button>

              <div className="modal-react">
                <FiMessageCircle />

                <span>
  {countComments(
    commentsMap[selectedPost.id] || []
  )}
</span>

                <small>
                  Comments
                </small>
              </div>

            </div>

            {/* DISCUSSION */}
            <div className="discussion-heading">
              <div>
                <span>
                  Class Discussion
                </span>

                <small>
                  Continue the conversation
                </small>
              </div>

             <strong>
  {countComments(
    commentsMap[selectedPost.id] || []
  )}
</strong>
            </div>

{/* COMMENTS */}
<div className="comments-area">
  {(commentsMap[selectedPost.id] || []).length === 0 ? (
    <div className="no-comments">
      <div className="no-comments-icon">
        <FiMessageCircle />
      </div>

      <strong>No discussion yet</strong>

      <span>
        Start the conversation with your classmates.
      </span>
    </div>
  ) : (
    <div className="comments-list">
      {(commentsMap[selectedPost.id] || []).map(
        (comment) => renderComment(comment)
      )}
    </div>
  )}
</div>

            {/* COMMENT INPUT */}
            <div className="comment-input">

              <div className="avatar input-avatar">

                {user?.profile_pic ? (
                  <img
                    src={user.profile_pic}
                    alt=""
                  />
                ) : (
                  <span>
                    {getInitial(
                      user?.full_name
                    )}
                  </span>
                )}

              </div>

              <div className="comment-input-box">

                <input
                  value={commentText}
                  onChange={(e) =>
                    setCommentText(
                      e.target.value
                    )
                  }
                  placeholder="Write a comment..."
                  onKeyDown={(e) => {
                    if (
                      e.key === "Enter" &&
                      !e.shiftKey
                    ) {
                      e.preventDefault();
                      handleComment();
                    }
                  }}
                />

               

                <button
                  type="button"
                  onClick={handleComment}
                  disabled={
                    !commentText.trim()
                  }
                >
                  <FiSend />
                </button>

              </div>

            </div>

          </div>
        </div>

      ) : (

        /* =================================================
           TEXT-ONLY POST MODAL
           ================================================= */
        <div className="memory-modal-content text-modal-content">

          {/* TEXT POST HEADER */}
<div className="text-post-header">
  <div className="modal-author">
    <div className="avatar modal-avatar">
      {selectedPost.profile_pic ? (
        <img
          src={selectedPost.profile_pic}
          alt=""
        />
      ) : (
        <span>
          {getInitial(selectedPost.full_name)}
        </span>
      )}
    </div>

    <div className="modal-author-info">
      <h3>
        {selectedPost.full_name}
      </h3>

      <span>
        {getPostMeta(selectedPost)}{" "}
        .{" "}
        {formatTime(selectedPost.created_at)}
      </span>
    </div>
  </div>

  {/* MORE OPTIONS */}
  {String(selectedPost.user_id) === String(user?.id) && (
    <div className="post-more-wrapper modal-more-wrapper">
      <button
        type="button"
        className="post-more"
        aria-label="More options"
        onClick={(e) => {
          e.stopPropagation();

          setOpenPostMenu(
            openPostMenu === `modal-${selectedPost.id}`
              ? null
              : `modal-${selectedPost.id}`
          );
        }}
      >
        <FiMoreHorizontal />
      </button>

      {openPostMenu === `modal-${selectedPost.id}` && (
        <div
          className="post-options-menu"
          onClick={(e) => e.stopPropagation()}
        >
<button
  type="button"
  className="post-option delete-option"
  onClick={() => handleDeleteClick(selectedPost)}
>
  <span className="post-option-icon">🗑️</span>
  <span>Delete Post</span>
</button>
        </div>
      )}
    </div>
  )}
</div>

          {/* LARGE TEXT */}
          <div className="text-post-body">

           

            <p>
              {selectedPost.content}
            </p>

          </div>

          {/* TEXT POST REACTIONS */}
          <div className="modal-reactions text-post-reactions">

            <button
              type="button"
              className={
                selectedPost.liked
                  ? "modal-react liked"
                  : "modal-react"
              }
              onClick={() =>
                handleReact(
                  selectedPost.id
                )
              }
            >
<FiHeart
  className="heart-icon"
  fill={
    selectedPost.liked
      ? "currentColor"
      : "none"
  }
/>

<span>
  {selectedPost.likes || 0}
</span>

<small>
  {selectedPost.liked ? "Liked" : "Likes"}
</small>
            </button>

            <div className="modal-react">

              <FiMessageCircle />

             <span>
  {countComments(
    commentsMap[selectedPost.id] || []
  )}
</span>

              <small>
                Comments
              </small>

            </div>

          </div>

          {/* DISCUSSION */}
          <div className="discussion-heading">

            <div>
              <span>
                Class Discussion
              </span>

              <small>
                Continue the conversation
              </small>
            </div>

           <strong>
  {countComments(
    commentsMap[selectedPost.id] || []
  )}
</strong>

          </div>

          {/* COMMENTS */}
<div className="comments-area">
  {(commentsMap[selectedPost.id] || []).length === 0 ? (
    <div className="no-comments">
      <div className="no-comments-icon">
        <FiMessageCircle />
      </div>

      <strong>No discussion yet</strong>

      <span>
        Start the conversation with your classmates.
      </span>
    </div>
  ) : (
    <div className="comments-list">
      {(commentsMap[selectedPost.id] || []).map(
        (comment) => renderComment(comment)
      )}
    </div>
  )}
</div>

          {/* COMMENT INPUT */}
          <div className="comment-input">

            <div className="avatar input-avatar">

              {user?.profile_pic ? (
                <img
                  src={user.profile_pic}
                  alt=""
                />
              ) : (
                <span>
                  {getInitial(
                    user?.full_name
                  )}
                </span>
              )}

            </div>

            <div className="comment-input-box">

              <input
                value={commentText}
                onChange={(e) =>
                  setCommentText(
                    e.target.value
                  )
                }
                placeholder="Write a comment..."
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    !e.shiftKey
                  ) {
                    e.preventDefault();
                    handleComment();
                  }
                }}
              />

              <FiSmile />

              <button
                type="button"
                onClick={handleComment}
                disabled={
                  !commentText.trim()
                }
              >
                <FiSend />
              </button>

            </div>

          </div>

        </div>
      )}

    </div>
  </div>
)}


{showLikesModal && likesModalPost && (
  <div
    className="likes-modal-overlay"
    onClick={closeLikesModal}
  >
    <div
      className="likes-modal"
      onClick={(e) => e.stopPropagation()}
    >
      {/* HEADER */}
      <div className="likes-modal-header">
        <div>
          <span className="likes-modal-kicker">
            REACTIONS
          </span>

          <h3>People who liked this</h3>

          <p>
            {likesModalPost.reactors?.length || 0}{" "}
            {likesModalPost.reactors?.length === 1
              ? "person"
              : "people"}{" "}
            liked this post.
          </p>
        </div>

        <button
          type="button"
          className="likes-modal-close"
          onClick={closeLikesModal}
          aria-label="Close likes"
        >
          <FiX />
        </button>
      </div>

      {/* PEOPLE */}
      <div className="likes-modal-list">
        {likesModalPost.reactors?.map((reactor) => (
          <button
            type="button"
            className="likes-person"
            key={reactor.user_id}
            onClick={() => {
              closeLikesModal();
              openStudentProfile(reactor);
            }}
          >
            <div className="likes-person-avatar">
              {reactor.profile_pic ? (
                <img
                  src={reactor.profile_pic}
                  alt={reactor.full_name}
                />
              ) : (
                <span>
                  {getInitial(reactor.full_name)}
                </span>
              )}
            </div>

            <div className="likes-person-info">
              <strong>
                {reactor.full_name}
              </strong>

              <span>
                Liked this post
              </span>
            </div>

            <FiHeart className="likes-person-heart" />
          </button>
        ))}
      </div>
    </div>
  </div>
)}

      {/* =====================================================
          CREATE MEMORY MODAL
          ===================================================== */}

{showDeleteModal && (
  <div className="delete-modal-overlay">
    <div className="delete-modal">
      <div className="delete-modal-icon">
        <FiTrash2 />
      </div>

      <h3>Delete Post?</h3>

      <p>
        Are you sure you want to delete this post? This action cannot be
        undone.
      </p>

      <div className="delete-modal-actions">
        <button
          className="delete-cancel-btn"
          onClick={() => {
            setShowDeleteModal(false);
            setPostToDelete(null);
          }}
        >
          Cancel
        </button>

        <button
          className="delete-confirm-btn"
          onClick={handleConfirmDelete}
        >
          Delete
        </button>
      </div>
    </div>
  </div>
)}


      {showCreateModal && (

        <div
          className="create-memory-overlay"
          onClick={closeCreateModal}
        >

          <div
            className="create-memory-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* ===============================================
                CREATE HEADER
                =============================================== */}

            <div className="create-memory-header">

              <div className="create-title-area">

                <span>
                  CREATE MEMORY
                </span>

                <h2>
                  Share with your batch
                </h2>

              </div>

              <button
                type="button"
                onClick={closeCreateModal}
              >
                <FiX />
              </button>

            </div>

            {/* ===============================================
                CREATE BODY
                =============================================== */}

            <div className="create-memory-body">

              <div className="create-memory-author">

                <div className="avatar create-avatar">

                  {user?.profile_pic ? (
                    <img
                      src={user.profile_pic}
                      alt=""
                    />
                  ) : (
                    <span>
                      {getInitial(
                        user?.full_name
                      )}
                    </span>
                  )}

                </div>

                <div>

                  <strong>
                    {user?.full_name ||
                      "Student"}
                  </strong>

<span>
  {isFaculty
    ? `${user?.department_program || "Faculty"} . Faculty`
    : `Class of ${user?.batch || "----"}`}
</span>

                </div>

              </div>

              <textarea
                className="memory-caption"
                placeholder="What's on your mind?"
                value={content}
                onChange={(e) =>
                  setContent(e.target.value)
                }
              />

              {/* =============================================
                  PHOTO PREVIEW
                  ============================================= */}

              {image ? (

                <div className="create-photo-preview">

                  <img
                    src={URL.createObjectURL(
                      image
                    )}
                    alt="Preview"
                  />

                  <button
                    type="button"
                    className="remove-preview"
                    onClick={() => {
                      setImage(null);

                      if (
                        fileInputRef.current
                      ) {
                        fileInputRef.current.value =
                          "";
                      }
                    }}
                  >
                    <FiX />
                  </button>

                  <div className="preview-photo-label">
                    <FiImage />
                    Photo attached
                  </div>

                </div>

              ) : (

                <button
                  type="button"
                  className="add-photo-area"
                  onClick={() =>
                    fileInputRef.current?.click()
                  }
                >

                  <div className="add-photo-icon">
                    <FiImage />
                  </div>

                  <strong>
                    Add a photo
                  </strong>

                  <span>
                    Share a moment from your
                    school year
                  </span>

                </button>

              )}

              {/* =============================================
                  CREATE ACTIONS
                  ============================================= */}

              <div className="create-memory-footer">

                <button
                  type="button"
                  className="add-photo-button"
                  onClick={() =>
                    fileInputRef.current?.click()
                  }
                >
                  <FiImage />

                  {image
                    ? "Change photo"
                    : "Add photo"}
                </button>

                <button
                  type="button"
                  className="share-memory-button"
                  disabled={
                    isPosting ||
                    (!content.trim() &&
                      !image)
                  }
                  onClick={handlePost}
                >
                  <FiSend />

                  {isPosting
                    ? "Sharing..."
                    : "Share"}
                </button>

              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}

export default StudentDashboard;
